import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { navigate } from 'wouter/use-location';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { ICmdTableCustomerCell, ICmdTableUserCell, TTagRecords } from './types';
import {
  TAG_ACTIVE_KEY,
  TAG_COLOR_KEY,
  TAG_CREATED_AT_KEY,
  TAG_CREATED_BY_KEY,
  TAG_CUSTOMER_ID_KEY,
  TAG_DELETED_AT_KEY,
  TAG_DETAIL_KEY,
  TAG_MODIFIED_AT_KEY,
  TAG_MODIFIED_BY_KEY,
  TAG_NAME_KEY,
  RIDE_TAG_TABLE_KEY,
} from './constants';
import { TABLE_CODEBOOKS_RIDE } from '../../Services/TableService';
import { URL_CODEBOOKS } from '../../router/constants';
import ThSearch from '../CmdTable/TableHeadCell/ThSearch';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import CustomerCell from '../CmdTable/TableCells/CustomerCell';
import ColorCell from '../CmdTable/TableCells/ColorCell';
import IconCell from '../CmdTable/TableCells/IconCell';
import DateTimeCell from '../CmdTable/TableCells/DateTimeCell';
import ThOnlySort from '../CmdTable/TableHeadCell/ThOnlySort';
import ThSimpleSelect from '../CmdTable/TableHeadCell/ThSimpleSelect';
import ThMultiSelect from '../CmdTable/TableHeadCell/ThMultiSelect';
import ThDateTime from '../CmdTable/TableHeadCell/ThDateTime';
import TextCell from '../CmdTable/TableCells/TextCell';
import DeleteAction from '../CmdTable/TableActions/DeleteAction';
import DetailAction from '../CmdTable/TableActions/DetailAction';
import { putRequest } from '../../Services/HttpService';
import { selectedCustomersAtom } from '../../store/recoil/customers';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
} from '../CmdTable/CmdTableState';
import useAnalytics from '../../hooks/useAnalytics';
import { getTableName } from '../CmdTable/CmdTableService';

interface IUseParkingTag {
  columns: ColumnDef<TTagRecords>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
}

export default function useParkingTag(): IUseParkingTag {
  const { formatMessage: f } = useIntl();
  const queryClient = useQueryClient();
  // const tableFiltersKey = useRecoilValue(CmdTableState.tableFilters);
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(RIDE_TAG_TABLE_KEY)
  );
  const selectedCustomers = useRecoilValue(selectedCustomersAtom);
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(RIDE_TAG_TABLE_KEY)
  );
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);
  const { trackEvent } = useAnalytics();

  const handleIndeterminateChange = (table) => {
    if (table.getIsSomeRowsSelected()) {
      // If currently indeterminate, unselect all.
      table.toggleAllRowsSelected(false);
    } else {
      // Otherwise, follow the default behavior.
      table.toggleAllRowsSelected(!table.getIsAllRowsSelected());
    }
  };

  const updateTable = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [RIDE_TAG_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
  }, [queryClient, filterForRequest, sortFilter, selectedCustomers]);

  const handleRemove = React.useCallback(
    async (id: number, customerId?: number | number[]) => {
      if (customerId && !Array.isArray(customerId)) {
        const ids = { [customerId]: [id] };
        const response = await putRequest(`/v1/${TABLE_CODEBOOKS_RIDE}/remove`, { ids });
        if (response) {
          updateTable();
        }
      }
    },
    [updateTable]
  );

  const handleAction = (id: number, customerId?: number) => {
    if (customerId && !Array.isArray(customerId)) {
      trackEvent([`table ${getTableName(TABLE_CODEBOOKS_RIDE)}`, 'click', 'Table action detail']);
      navigate(`${URL_CODEBOOKS}/${TABLE_CODEBOOKS_RIDE}/edit/${id}?customerId=${customerId}`);
    }
  };

  const columns = React.useMemo<ColumnDef<TTagRecords>[]>(
    () => [
      // {
      //   id: 'select',
      //   header: ({ table }) => (
      //     <IndeterminateCheckbox
      //       {...{
      //         checked: table.getIsAllRowsSelected(),
      //         indeterminate: table.getIsSomeRowsSelected(),
      //         // onChange: table.getToggleAllRowsSelectedHandler(),
      //         onChange: () => handleIndeterminateChange(table),
      //       }}
      //     />
      //   ),
      //   cell: ({ row }) => (
      //     <div className="px-1">
      //       <IndeterminateCheckbox
      //         {...{
      //           checked: row.getIsSelected(),
      //           disabled: !row.getCanSelect(),
      //           indeterminate: row.getIsSomeSelected(),
      //           onChange: row.getToggleSelectedHandler(),
      //         }}
      //       />
      //     </div>
      //   ),
      //   size: 30,
      // },
      {
        accessorKey: TAG_NAME_KEY,
        header: (table) => (
          <ThSearch
            tableName={RIDE_TAG_TABLE_KEY}
            id={TAG_NAME_KEY}
            title={f({ id: `table_list.columns.rideTags.${TAG_NAME_KEY}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_TAG_NAME_KEY}.tooltip` })}
            isSortable={true}
            // minWidth="176px"
            // maxWidth="300px"
          />
        ),
        cell: (info) =>
          info.row.original?.name ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 180,
      },
      {
        accessorKey: TAG_CUSTOMER_ID_KEY,
        header: (table) => (
          <ThSimpleSelect
            tableName={RIDE_TAG_TABLE_KEY}
            id={TAG_CUSTOMER_ID_KEY}
            title={f({ id: `table_list.columns.${TAG_CUSTOMER_ID_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_TAG_CUSTOMER_ID_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.customerId ? (
            <CustomerCell customer={info.getValue<ICmdTableCustomerCell>()} />
          ) : (
            <TextCell text="" />
          ),
        // size: 260,
        minSize: 200,
      },
      {
        accessorKey: TAG_COLOR_KEY,
        header: (table) => (
          <ThOnlySort
            tableName={RIDE_TAG_TABLE_KEY}
            id={TAG_COLOR_KEY}
            title={f({ id: `table_list.columns.parkingTags.${TAG_COLOR_KEY}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_TAG_COLOR_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.color ? (
            <ColorCell color={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        size: 80,
      },
      {
        accessorKey: TAG_ACTIVE_KEY,
        header: (table) => (
          <ThSimpleSelect
            tableName={RIDE_TAG_TABLE_KEY}
            id={TAG_ACTIVE_KEY}
            title={f({ id: `table_list.columns.parkingTags.${TAG_ACTIVE_KEY}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_TAG_ACTIVE_KEY}.tooltip` })}
            // isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.active ? (
            <IconCell isIcon={info.getValue<boolean>()} icon="cmd-icon-active text-success" />
          ) : (
            <TextCell text="" />
          ),
        size: 100,
      },
      {
        accessorKey: TAG_CREATED_BY_KEY,
        header: (table) => (
          <ThMultiSelect
            tableName={RIDE_TAG_TABLE_KEY}
            id={TAG_CREATED_BY_KEY}
            title={f({ id: `table_list.columns.${TAG_CREATED_BY_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_TAG_CREATED_BY_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdBy ? (
            <TextCell text={info.getValue<ICmdTableUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: TAG_CREATED_AT_KEY,
        header: (table) => (
          <ThDateTime
            tableName={RIDE_TAG_TABLE_KEY}
            id={TAG_CREATED_AT_KEY}
            title={f({ id: `table_list.columns.${TAG_CREATED_AT_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_TAG_CREATED_AT_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdAt ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: TAG_MODIFIED_BY_KEY,
        header: (table) => (
          <ThMultiSelect
            tableName={RIDE_TAG_TABLE_KEY}
            id={TAG_MODIFIED_BY_KEY}
            title={f({ id: `table_list.columns.${TAG_MODIFIED_BY_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_TAG_MODIFIED_BY_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <TextCell text={info.getValue<ICmdTableUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: TAG_MODIFIED_AT_KEY,
        header: (table) => (
          <ThDateTime
            id={TAG_MODIFIED_AT_KEY}
            tableName={RIDE_TAG_TABLE_KEY}
            title={f({ id: `table_list.columns.${TAG_MODIFIED_AT_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_TAG_MODIFIED_AT_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },

      {
        accessorKey: TAG_DETAIL_KEY,
        header: () => (
          <div className="w-sm text-center th-sticky sticky-col detail">
            {f({ id: 'table.detailBtn' })}
          </div>
        ),
        cell: (info) =>
          info.row.original ? (
            <DetailAction
              id={info.row.original.id}
              tableName={TABLE_CODEBOOKS_RIDE}
              customerId={info.row.original.customerId.id}
              onClickCallback={handleAction}
            />
          ) : null,
        size: 60, // Adjusted for consistency with header styling
        enablePinning: true,
      },
      {
        accessorKey: TAG_DELETED_AT_KEY,
        header: () => (
          <div className="w-sm text-center th-sticky sticky-col detail">
            {f({ id: 'table.deleteBtn' })}
          </div>
        ),
        cell: (info) =>
          info.row.original ? (
            <DeleteAction
              id={info.row.original.id}
              tableName={TABLE_CODEBOOKS_RIDE}
              customerId={info.row.original.customerId.id}
              onClickCallback={handleRemove}
              showConfirmDialog={true}
            />
          ) : null,
        size: 80, // Adjusted for consistency with header styling
        enablePinning: true,
      },
    ],
    [f, handleRemove]
  );

  const columnOrder = React.useMemo(() => {
    const order = columnsTable.map((item) => item.key);
    // add the detail and delete columns to the end
    order.push(TAG_DETAIL_KEY, TAG_DELETED_AT_KEY);
    return order;
  }, [columnsTable]);

  const columnVisibility = React.useMemo(() => {
    return columnsTable.reduce((acc, column) => {
      acc[column.key] = !column.hidden;
      return acc;
    }, {});
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
  };
}
